import { AnalyticsEvent } from "../types/Analytics";

// Rule-based recommendations for the Analytics screen. Everything is computed
// from the raw event list so the tips always match the charts on screen.
// Conversion rules use unique users (a user tapping a button five times is
// still one user), and every rule has a minimum sample size so tiny
// communities get "collect more data" instead of noise.

export type InsightPriority = "high" | "medium" | "low";

export type Insight = {
  id: string;
  priority: InsightPriority;
  area: string;
  title: string;
  why: string;
  action: string;
};

export type HealthMetrics = {
  dau: number;
  wau: number;
  mau: number;
  stickinessPct: number;
  wauPrev: number;
  wauChangePct: number | null;
};

const DAY = 24 * 60 * 60 * 1000;

// Per event: unique users when the event carries userIds, raw event count
// otherwise (pre-auth events like app_install and onboarding_start are logged
// without a userId).
const effectiveCountsFor = (
  events: AnalyticsEvent[]
): ((event: string) => number) => {
  const users = new Map<string, Set<string>>();
  const counts = new Map<string, number>();
  events.forEach((e) => {
    counts.set(e.event, (counts.get(e.event) || 0) + 1);
    if (!e.userId) return;
    let set = users.get(e.event);
    if (!set) {
      set = new Set();
      users.set(e.event, set);
    }
    set.add(e.userId);
  });
  return (event: string) => {
    const u = users.get(event)?.size || 0;
    return u > 0 ? u : counts.get(event) || 0;
  };
};

export const calculateHealthMetrics = (
  events: AnalyticsEvent[],
  now: number = Date.now()
): HealthMetrics => {
  const dau = new Set<string>();
  const wau = new Set<string>();
  const mau = new Set<string>();
  const wauPrev = new Set<string>();

  events.forEach((e) => {
    if (!e.userId) return;
    if (e.ts >= now - 30 * DAY) mau.add(e.userId);
    if (e.ts >= now - 7 * DAY) wau.add(e.userId);
    if (e.ts >= now - DAY) dau.add(e.userId);
    if (e.ts >= now - 14 * DAY && e.ts < now - 7 * DAY) wauPrev.add(e.userId);
  });

  return {
    dau: dau.size,
    wau: wau.size,
    mau: mau.size,
    stickinessPct: mau.size > 0 ? (dau.size / mau.size) * 100 : 0,
    wauPrev: wauPrev.size,
    wauChangePct:
      wauPrev.size > 0
        ? ((wau.size - wauPrev.size) / wauPrev.size) * 100
        : null,
  };
};

type RateRule = {
  id: string;
  area: string;
  from: string;
  to: string;
  // Fire when conversion (unique users) is below this percentage.
  threshold: number;
  minSample: number;
  priority: InsightPriority;
  title: string;
  action: string;
  fromLabel: string;
  toLabel: string;
};

const RATE_RULES: RateRule[] = [
  {
    id: "onboarding-completion",
    area: "Onboarding",
    from: "onboarding_start",
    to: "onboarding_finish",
    threshold: 60,
    minSample: 10,
    priority: "high",
    title: "Onboarding loses too many people",
    action:
      "Shorten the questionnaire, show progress, and move optional questions to after signup. Test skipping straight to the first plan.",
    fromLabel: "started onboarding",
    toLabel: "finished it",
  },
  {
    id: "signup-conversion",
    area: "Onboarding",
    from: "onboarding_finish",
    to: "signup_success",
    threshold: 50,
    minSample: 10,
    priority: "high",
    title: "People finish onboarding but do not sign up",
    action:
      "Reduce signup friction: offer Apple/Google one-tap sign-in first and delay email confirmation until after the first session.",
    fromLabel: "finished onboarding",
    toLabel: "created an account",
  },
  {
    id: "paywall-reach",
    area: "Monetization",
    from: "signup_success",
    to: "paywall_view",
    threshold: 40,
    minSample: 10,
    priority: "medium",
    title: "Few users ever see the paywall",
    action:
      "Surface the paywall from more moments: after finishing the first step, when opening a locked plan, and from the profile screen.",
    fromLabel: "signed up",
    toLabel: "viewed the paywall",
  },
  {
    id: "trial-conversion",
    area: "Monetization",
    from: "paywall_view",
    to: "trial_start",
    threshold: 5,
    minSample: 20,
    priority: "high",
    title: "The paywall is not converting",
    action:
      "Rework the paywall: lead with the outcome the coach promises, show social proof, and test a shorter price list with a highlighted annual plan.",
    fromLabel: "viewed the paywall",
    toLabel: "started a trial",
  },
  {
    id: "trial-to-paid",
    area: "Monetization",
    from: "trial_start",
    to: "subscription_active",
    threshold: 30,
    minSample: 5,
    priority: "high",
    title: "Trials are not becoming subscriptions",
    action:
      "Make the trial week count: a welcome push sequence, a day-1 quick win, and a reminder before the trial ends with the value they got.",
    fromLabel: "started a trial",
    toLabel: "became subscribers",
  },
  {
    id: "landing-cta",
    area: "Landing page",
    from: "landing_view",
    to: "landing_cta_press",
    threshold: 10,
    minSample: 50,
    priority: "medium",
    title: "The landing page is not converting visitors",
    action:
      "Tighten the hero: one clear promise, one CTA above the fold, and proof (results, testimonials) right under it.",
    fromLabel: "viewed the landing page",
    toLabel: "pressed the CTA",
  },
  {
    id: "plan-start",
    area: "Learning plans",
    from: "EXPLORE_PLAN_PREVIEW",
    to: "START_PLAN",
    threshold: 30,
    minSample: 10,
    priority: "medium",
    title: "Plan previews are not converting to starts",
    action:
      "Strengthen plan previews: clearer outcome promise, expected time per day, and a visible first-day sample before asking to start.",
    fromLabel: "viewed a plan",
    toLabel: "started one",
  },
  {
    id: "first-step",
    area: "Learning plans",
    from: "START_PLAN",
    to: "START_STEP",
    threshold: 60,
    minSample: 10,
    priority: "high",
    title: "Users start plans but never do the first step",
    action:
      "Take users straight into day 1 after starting a plan instead of back to the plan page, and send a push if the first step is untouched after a day.",
    fromLabel: "started a plan",
    toLabel: "began the first step",
  },
];

export const generateInsights = (
  events: AnalyticsEvent[],
  health: HealthMetrics
): Insight[] => {
  const insights: Insight[] = [];
  const count = effectiveCountsFor(events);

  if (events.length === 0) {
    return [
      {
        id: "no-data",
        priority: "high",
        area: "Data",
        title: "No analytics events in this range",
        why: "There is nothing to analyze yet for the selected period.",
        action:
          "Check that the app build is logging events, or widen the date range.",
      },
    ];
  }

  RATE_RULES.forEach((rule) => {
    const from = count(rule.from);
    const to = count(rule.to);
    if (from < rule.minSample) return;
    const rate = (to / from) * 100;
    if (rate >= rule.threshold) return;
    insights.push({
      id: rule.id,
      priority: rule.priority,
      area: rule.area,
      title: rule.title,
      why: `${from.toLocaleString()} users ${rule.fromLabel} but only ${to.toLocaleString()} (${rate.toFixed(1)}%) ${rule.toLabel}. Healthy is above ${rule.threshold}%.`,
      action: rule.action,
    });
  });

  // Engagement habits: stickiness says how many monthly users come back daily.
  if (health.mau >= 20 && health.stickinessPct < 20) {
    insights.push({
      id: "stickiness",
      priority: "high",
      area: "Engagement",
      title: "Users are not coming back daily",
      why: `Only ${health.dau.toLocaleString()} of ${health.mau.toLocaleString()} monthly users (${health.stickinessPct.toFixed(1)}%) were active in the last day. Healthy habit apps sit above 20%.`,
      action:
        "Lean on the daily loop: streaks, a fixed daily step reminder push, and content that changes every day.",
    });
  }

  // Week-over-week trend.
  if (health.wauChangePct !== null && health.wauPrev >= 10) {
    if (health.wauChangePct <= -20) {
      insights.push({
        id: "wau-drop",
        priority: "high",
        area: "Engagement",
        title: "Weekly active users are dropping",
        why: `WAU fell from ${health.wauPrev.toLocaleString()} to ${health.wau.toLocaleString()} (${health.wauChangePct.toFixed(0)}%) week over week.`,
        action:
          "Send a re-engagement push and email to users inactive for 7+ days, and check whether a recent release broke a key flow.",
      });
    } else if (health.wauChangePct >= 20) {
      insights.push({
        id: "wau-growth",
        priority: "low",
        area: "Engagement",
        title: "Weekly active users are growing",
        why: `WAU grew from ${health.wauPrev.toLocaleString()} to ${health.wau.toLocaleString()} (+${health.wauChangePct.toFixed(0)}%) week over week.`,
        action:
          "Double down on whatever drove the spike: check top events and recent campaigns, and capture these users with the welcome email sequence.",
      });
    }
  }

  // Growth: signups in range.
  const signups = count("signup_success");
  const installs = count("app_install");
  if (installs >= 10 && signups === 0) {
    insights.push({
      id: "no-signups",
      priority: "high",
      area: "Growth",
      title: "Installs are not turning into accounts",
      why: `${installs.toLocaleString()} installs in this range produced zero signups.`,
      action:
        "Walk the install-to-signup path on a fresh device. Something is broken or the signup ask comes too early.",
    });
  } else if (installs === 0 && signups === 0) {
    insights.push({
      id: "no-growth",
      priority: "medium",
      area: "Growth",
      title: "No new installs in this range",
      why: "No app_install or signup_success events were recorded.",
      action:
        "Push acquisition: share the landing page, run the social schedule, and make sure the app store listing is live and optimized.",
    });
  }

  // Community engagement.
  const questionUsers =
    count("SEND_QUESTION") + count("START_QUESTION") + count("OPEN_ROOM");
  if (health.wau >= 20 && questionUsers === 0) {
    insights.push({
      id: "community-quiet",
      priority: "medium",
      area: "Community",
      title: "Nobody is using the community features",
      why: `${health.wau.toLocaleString()} weekly active users but no question or room activity.`,
      action:
        "Have the coach seed it: post a weekly question, reply fast, and mention the community inside daily steps.",
    });
  }

  // Paywall declines with no winback catch.
  const paywallDeclines = count("paywall_declined");
  const winbackViews = count("winback_offer_view");
  if (paywallDeclines >= 10 && winbackViews === 0) {
    insights.push({
      id: "winback-missing",
      priority: "medium",
      area: "Monetization",
      title: "Paywall declines are not being caught",
      why: `${paywallDeclines.toLocaleString()} users declined the paywall and none saw a winback offer.`,
      action:
        "Enable the winback offer so a decline triggers a discounted second chance instead of a dead end.",
    });
  }

  // Notifications permission is the retention lever.
  const notifPerm = count("GET_NOTIF_PERMISSION");
  if (signups >= 10 && notifPerm / signups < 0.5) {
    insights.push({
      id: "notif-permission",
      priority: "medium",
      area: "Retention",
      title: "Most users never grant notification permission",
      why: `${notifPerm.toLocaleString()} of ${signups.toLocaleString()} new users hit the notification permission step.`,
      action:
        "Ask for notifications after a moment of value (finishing the first step), with a screen that previews what the reminders look like.",
    });
  }

  const order: Record<InsightPriority, number> = { high: 0, medium: 1, low: 2 };
  insights.sort((a, b) => order[a.priority] - order[b.priority]);

  if (insights.length === 0) {
    insights.push({
      id: "all-healthy",
      priority: "low",
      area: "Overview",
      title: "No red flags in this range",
      why: "Every funnel with enough data is above its healthy threshold.",
      action:
        "Focus on growth: more installs through content, ads, or the landing page. Re-check after the next push.",
    });
  }

  return insights;
};
